import React, { useEffect, useState } from "react";
import { api, Banner, Button, Field } from "./ui.tsx";
import type { User } from "../shared/contracts.ts";
import type { OfflinePolicy } from "../server/offline.ts";
import {
  clearOffline,
  localOfflineEnabled,
  offlinePending,
  openPending,
  removePending,
  setLocalOffline,
} from "./offline-store.ts";
export function OfflineSettings({ user }: { user: User }) {
  const [policy, setPolicy] = useState<OfflinePolicy | null>(null),
    [enabled, setEnabled] = useState(false),
    [rows, setRows] = useState<Awaited<ReturnType<typeof offlinePending>>>([]),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const load = async () => {
    setRows(await offlinePending(user.id));
    setEnabled(await localOfflineEnabled(user.id));
  };
  useEffect(() => {
    void load().catch(() =>
      setMessage("Armazenamento local indisponível neste navegador."),
    );
    api<OfflinePolicy>("/offline/policy")
      .then(setPolicy)
      .catch((e) => setMessage(e.message));
    const changed = () => void load();
    window.addEventListener("offline-pending-changed", changed);
    return () => window.removeEventListener("offline-pending-changed", changed);
  }, [user.id]);
  async function sync() {
    setBusy(true);
    setMessage("");
    let confirmed = 0;
    try {
      const current = await api<OfflinePolicy>("/offline/policy");
      setPolicy(current);
      if (!current.enabled)
        throw Error("O administrador não autorizou o modo offline.");
      for (const row of rows) {
        const body = await openPending(row);
        await api("/offline/demands", "POST", body);
        await removePending(row.id);
        confirmed++;
      }
      setMessage(
        `${confirmed} ${confirmed === 1 ? "demanda confirmada" : "demandas confirmadas"} no servidor.`,
      );
    } catch (e) {
      setMessage(
        (confirmed ? `${confirmed} confirmadas. ` : "") +
          (e as Error).message +
          " As demais pendências foram preservadas.",
      );
    } finally {
      await load();
      setBusy(false);
    }
  }
  return (
    <section className="progress-card">
      <h2>Pendências offline</h2>
      <p>
        Para o cadastro de demandas na tela já aberta. Somente os dados que você
        escolher salvar ficam neste aparelho, protegidos por criptografia local.
        Não inclui novos anexos offline.
      </p>
      {user.admin && policy && (
        <details>
          <summary>Autorização administrativa</summary>
          <Field label="Permitir cadastro pendente no aparelho">
            <input
              type="checkbox"
              checked={policy.enabled}
              onChange={(e) =>
                setPolicy({ ...policy, enabled: e.target.checked })
              }
            />
          </Field>
          <Field label="Validade do armazenamento">
            <select
              value={policy.retentionHours}
              onChange={(e) =>
                setPolicy({ ...policy, retentionHours: Number(e.target.value) })
              }
            >
              {[24, 48, 72].map((n) => (
                <option key={n} value={n}>
                  {n} horas
                </option>
              ))}
            </select>
          </Field>
          <Button
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                setPolicy(await api("/offline/policy", "PUT", policy));
                setMessage("Política offline publicada.");
              } catch (e) {
                setMessage((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Publicar autorização
          </Button>
        </details>
      )}
      <label>
        <input
          type="checkbox"
          checked={enabled}
          disabled={!policy?.enabled || busy}
          onChange={async (e) => {
            const next = e.target.checked;
            if (
              !next &&
              rows.length &&
              !window.confirm(
                "Desativar e remover as pendências deste aparelho?",
              )
            )
              return;
            try {
              await setLocalOffline(user.id, next);
              await load();
            } catch (e) {
              setMessage((e as Error).message);
            }
          }}
        />{" "}
        Permitir salvar minhas pendências neste aparelho
      </label>
      {!policy?.enabled && (
        <p>
          O armazenamento offline precisa ser autorizado pelo administrador.
        </p>
      )}
      <p>{rows.length} pendências aguardando envio.</p>
      {rows.length > 0 && (
        <>
          <Button busy={busy} onClick={() => void sync()}>
            Enviar pendências
          </Button>
          <ul>
            {rows.map((r, i) => (
              <li key={r.id}>
                Pendência {i + 1} · {new Date(r.at).toLocaleString("pt-BR")} ·{" "}
                {r.expires < Date.now()
                  ? "Validade expirada"
                  : "Aguardando confirmação"}{" "}
                <Button
                  disabled={busy}
                  onClick={async () => {
                    if (
                      window.confirm(
                        "Remover esta pendência do aparelho? Ela não será enviada.",
                      )
                    )
                      await removePending(r.id);
                  }}
                >
                  Remover
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="small muted">
        Ao sair da conta, as pendências deste aparelho são removidas. Envie-as
        antes de sair. O registro e suas datas só são confirmados pelo servidor,
        após validar suas permissões.
      </p>
      {message && <Banner>{message}</Banner>}
    </section>
  );
}
